import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData, AuditTrailForExport } from "../util/getAuditTrailData";
import { getLoggedInUserInfo, uploadJsonToS3 } from "../util/AppUtils";


const baseUrl = BASE_URL + "procedure-task";

// 📌 Get Reassign Value
export const getReassignValue = () => {
  const userInfo = getLoggedInUserInfo();
  return userInfo?.permission_to_edit?.Reassign === true // || userInfo?.permission_to_edit?.Admin === true;
};

let reassign = getReassignValue();

// 📌 Create Task (POST)
export const createTask = createAsyncThunk(
  "tasks/create",
  async (taskData, { rejectWithValue }) => {
    try {
      const version_content = {
        ...taskData,
        action_to_show: `Created Task "${taskData.task}"`,
      };

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "task_created",
        "CREATE",
        true
      );

      const ad = await getAuditTrailData({
        action: "CREATE_TASK",
        comment: "Created Task",
        version_content: {
          action_to_show: version_content.action_to_show,
        },
        new_content_snapshot,
      });

      const response = await axios.post(`${baseUrl}/create`, {
        ...taskData,
        audit_trail_data: ad,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error creating task");
    }
  }
);

// 📌 Update Task (PUT)
export const updateTask = createAsyncThunk(
  "tasks/update",
  async (
    { task_id, data, newTaskInfo, previusTaskInfo },
    { rejectWithValue }
  ) => {
    try {
      const version_content = {
        ...newTaskInfo,
        action_to_show: "Edit Task Pop-Up Table",
        previusTaskInfo,
      };

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "task_updated",
        "UPDATE",
        true
      );

      const ad = await getAuditTrailData({
        action: "UPDATE_TASK",
        comment: "Updated Task",
        version_content: {
          action_to_show: version_content.action_to_show,
        },
        new_content_snapshot,
      });

      const response = await axios.put(`${baseUrl}/update/${task_id}`, {
        ...data,
        audit_trail_data: ad,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating task");
    }
  }
);

// 📌 Update Task Pin (PUT)
export const updateTaskPin = createAsyncThunk(
  "tasks/updatePin",
  async (
    { task_id, is_pinned, taskInfo, taskreference },
    { rejectWithValue }
  ) => {
    try {
      const version_content = {
        ...taskInfo,
        is_pinned: is_pinned,
        action_to_show: "UPDATE_TASK",
        previusTaskInfo: {
          ...taskInfo,
          reference: taskreference,
        },
      };

      const new_content_snapshot = await uploadJsonToS3(
        version_content,
        "task_pin_updated",
        "UPDATE",
        true
      );

      const ad = await getAuditTrailData({
        action: "UPDATE_TASK",
        comment: "Updated Task",
        version_content: {
          action_to_show: version_content.action_to_show,
        },
        new_content_snapshot,
      });

      const response = await axios.put(`${baseUrl}/update-pin/${task_id}`, {
        is_pinned: is_pinned,
        audit_trail_data: ad,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error updating task pin");
    }
  }
);

// 📌 Get Task by ID (GET)
export const getTaskById = createAsyncThunk(
  "tasks/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-by-id/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching task");
    }
  }
);

// 📌 Fetch All Tasks (GET)
export const fetchtAllTasks = createAsyncThunk(
  "tasks/getAll",
  async ({ searchQuery, action_item_id }, { rejectWithValue }) => {
    try {
      let response = {}

      const user_id = `user_id=${localStorage.getItem("user_id")}`
      // If action item id is present, fetch the task for that id
      if (action_item_id) {
        response = await axios.get(`${baseUrl}/get-by-id/${action_item_id}`);
      } else {
        
        response = await axios.get(`${baseUrl}/get-all?${user_id}&is_reassign=${reassign}&${searchQuery}`);
      }

      if (action_item_id) {
        return {...response.data, pagination: { total: 1 }};
      }else{
        return response.data
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching tasks");
    }
  }
);

// 📌 Fetch All Action Items (GET)
export const fetchAllActionItems = createAsyncThunk(
  "tasks/getAllActionItems",
  async ({ searchQuery }, { rejectWithValue }) => {
    try {
      let reassign = getReassignValue();
      const response = await axios.get(`${baseUrl}/get-all-active?user_id=${localStorage.getItem("user_id")}&is_reassign=${reassign}${searchQuery && `&${searchQuery}`}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching action items");
    }
  }
);

// 📌 Fetch Action Items With Filter (GET)
export const fetchActionItemsWithFilter = createAsyncThunk(
  "tasks/getAllActionItemsWithFilter",
  async ({ filter }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-all-active?user_id_ref=${localStorage.getItem("user_id")}&${filter}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching action items with filter");
    }
  }
);

// 📌 Fetch Action Items Count Info (GET)
export const fetchActionItemsCountInfo = createAsyncThunk(
  "tasks/getActionItemsCountInfo",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-counts?user_id_ref=${localStorage.getItem("user_id")}&is_reassign=${reassign}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching action items count info");
    }
  }
);

// 📌 Fetch Tasks by Procedure Ltree Ref (GET)
export const fetchTasksByLtreeRef = createAsyncThunk(
  "tasks/getByLtreeRef",
  async ({ procedure_ltree_ref }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${baseUrl}/get-all?user_id=${localStorage.getItem("user_id")}&is_reassign=${getLoggedInUserInfo()?.permission_to_edit?.Reassign === true}&procedure_ltree_ref=${procedure_ltree_ref}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching tasks by ltree ref");
    }
  }
);

// 📌 Fetch Tasks by User ID (GET) - Fetches tasks where user is assignee or owner
export const fetchTasksByUserId = createAsyncThunk(
  "tasks/getByUserId",
  async ({ user_id }, { rejectWithValue }) => {
    try {
      // Fetch tasks where user is assignee or owner
      // Backend should handle OR logic for assign_to and owned_by
      const response = await axios.get(`${baseUrl}/get-all?user_id=${user_id}&is_reassign=false`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching tasks by user id");
    }
  }
);

// 📌 Fetch Reassigned Tasks (GET) - Fetches tasks that need reassignment
export const fetchReassignedTasks = createAsyncThunk(
  "tasks/getReassigned",
  async ({ queryParams }, { rejectWithValue }) => {
    try {
      // Logic for fetching reassigned tasks
      // Assuming queryParams string like "is_reassigned=true" is passed or we construct it here
      const queryString = queryParams || "is_reassigned=true";
      const response = await axios.get(`${baseUrl}/get-all-active?${queryString}&user_id=${localStorage.getItem("user_id")}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching reassigned tasks");
    }
  }
);

// 📌 Fetch Tasks with Custom Query (GET) - Flexible function to fetch tasks with any custom query parameters
export const fetchTasksWithCustomQuery = createAsyncThunk(
  "tasks/getWithCustomQuery",
  async ({ queryParams, endpoint = "get-all", pagination = true }, { rejectWithValue }) => {
    try {
      let queryString = "";

      // If queryParams is a string, use it directly
      if (typeof queryParams === "string") {
        queryString = queryParams;
      }
      // If queryParams is an object, convert it to query string
      else if (typeof queryParams === "object" && queryParams !== null) {
        const params = new URLSearchParams();
        Object.keys(queryParams).forEach(key => {
          if (queryParams[key] !== null && queryParams[key] !== undefined) {
            params.append(key, queryParams[key]);
          }
        });
        queryString = params.toString();
      }

      // Build the URL with default parameters if not provided
      const defaultParams = [];
      if (!queryString.includes("user_id=")) {
        defaultParams.push(`user_id=${localStorage.getItem("user_id")}`);
      }
      if (!queryString.includes("is_reassign=")) {
        defaultParams.push(`is_reassign=${getLoggedInUserInfo()?.permission_to_edit?.Reassign === true}`);
      }

      const baseQuery = defaultParams.length > 0 ? `${defaultParams.join("&")}${queryString ? `&${queryString}` : ""}` : queryString;
      let url = `${baseUrl}/${endpoint}${baseQuery ? `?${baseQuery}` : ""}`;

      if (!pagination) {
        const urlObj = new URL(url);
        urlObj.searchParams.set("limit", "100000");
        url = urlObj.toString();
      }

      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error fetching tasks with custom query");
    }
  }
);


// 📌 Delete Task (DELETE)
export const deleteTask = createAsyncThunk(
  "tasks/delete",
  async ({ task_id, task_name }, { rejectWithValue }) => {
    try {
      const ad = await getAuditTrailData({
        action: "DELETE_TASK",
        comment: "Deleted Task",
        version_content: {
          task_id: task_id,
          action_to_show: `Deleted Task "${task_name}"`
        }
      });
      const response = await axios.post(`${baseUrl}/delete/${task_id}`, {
        task_id: task_id,
        task_name: task_name,
        audit_trail_data: ad
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error deleting task");
    }
  }
);

// 📌 Export CSV (GET)
export const exportCSV = createAsyncThunk(
  "tasks/exportCSV",
  async (_, { rejectWithValue }) => {
    try {
      await AuditTrailForExport({
        apiUrl: `${baseUrl}/export`,
        file_name: "task-data-" + Date.now() + ".csv",
        action: "EXPORT_TASK_DATA",
        action_to_show: "Task Table data exported as CSV",
        additionalData: {
          user_id: localStorage.getItem("user_id"),
          is_reassign: reassign.toString()
        }
      })
      return [];
    } catch (error) {
      return rejectWithValue(error.response?.data || "Error exporting CSV");
    }
  }
);

// 🛠 Tasks Slice
const tasksSlice = createSlice({
  name: "tasks",
  initialState: {
    tasks: [],
    action_items: [],
    task: null,
    loading: false,
    error: null,
    taskInfo: null, //User to store reference and tree_node_id
    taskReference: {
      task_reference: null,
      task_reference_id: null,
      task_tree_node_id_ref: null,
      procedure_ltree_ref: null,
      is_revision:false,
    },
    isTaskPopupOpen: false,
    action_item_id: null,
    copiedAndRedirecred: false,
    count_info: {
      due_count: 0,
      overdue_count: 0,
      reassigned_count: 0,
      rev_count: 0
    },
    counts_in_filter: {
      archivedCount: 0,
      assignedCount: 0,
      ownedCount: 0,
      releasedCount: 0,
      reassignCount: 0,
      totalOwnedOrAssignedCount: 0,
    },
    newTask: null,
    redirectFilter: null,
    pendingCount:0,
  },
  reducers: {
    setNewTask: (state, action) => {
      state.newTask = action.payload;
    },
    setTaskInfo: (state, action) => {
      state.taskInfo = action.payload
    },
    setNullInfo: (state) => {
      state.taskInfo = null
    },
    setTaskReference: (state, action) => {
      state.taskReference = {
        task_reference: action.payload.task_reference,
        task_reference_id: action.payload.task_reference_id,
        task_tree_node_id_ref: action.payload.task_tree_node_id_ref,
        procedure_ltree_ref: action.payload.procedure_ltree_ref,
        is_revision: action.payload.is_revision
      }
    },
    clearTaskReference: (state) => {
      state.taskReference = {
        task_reference: null,
        task_reference_id: null,
        task_tree_node_id_ref: null,
        procedure_ltree_ref: null,
        is_revision:false,
      }
    },
    openTaskPopup: (state) => {
      state.isTaskPopupOpen = true
    },
    closeTaskPopup: (state) => {
      state.isTaskPopupOpen = false
      state.action_item_id = null
    },
    setActionItemId: (state, action) => {
      state.action_item_id = action.payload
    },
    setCopiedAndRedirect: (state, action) => {
      state.copiedAndRedirecred = action.payload
    },
    setRedirectFilter: (state, action) => {
      state.redirectFilter = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      // 📌 Fulfilled Cases (Data Handling)
      .addCase(getTaskById.fulfilled, (state, action) => {
        state.task = action.payload;
      })
      .addCase(fetchtAllTasks.fulfilled, (state, action) => {
        state.tasks = action.payload.results;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchActionItemsCountInfo.fulfilled, (state, action) => {
        state.count_info = {
          due_count: action.payload.results.due_count,
          overdue_count: action.payload.results.overdue_count,
          reassigned_count: action.payload.results.reassigned_count,
          rev_count: action.payload.results.rev_count
        };
      })
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.tasks = state.tasks.filter((task) => task.id !== action.payload);
      })
      .addCase(updateTaskPin.fulfilled, (state, action) => {
        const response = action.payload;
        if (response?.results) {
          const updatedTask = response.results;
          const taskId = updatedTask.task_id;
          if (taskId) {
            const index = state.tasks.findIndex((task) => task.task_id === taskId);
            if (index !== -1) {
              state.tasks[index] = { ...state.tasks[index], is_pinned: updatedTask.is_pinned };
            }
          }
        }
      })
      .addCase(fetchTasksByUserId.fulfilled, (state, action) => {
        state.tasks = action.payload.results || [];
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchTasksWithCustomQuery.fulfilled, (state, action) => {
        state.tasks = action.payload.results || [];
        state.pagination = action.payload.pagination;
        state.pendingCount = action.payload.pagination.pendingCount;
      })
      // Grouping Action Items Logic
      .addMatcher(
        isAnyOf(
          fetchAllActionItems.fulfilled,
          fetchReassignedTasks.fulfilled,
          fetchActionItemsWithFilter.fulfilled
        ),
        (state, action) => {
          state.action_items = action.payload.results;
          state.pagination = action.payload.pagination;
          state.counts_in_filter = {
            archivedCount: action.payload.archivedCount,
            assignedCount: action.payload.assignedCount,
            ownedCount: action.payload.ownedCount,
            releasedCount: action.payload.releasedCount,
            reassignCount: action.payload.reassignCount,
            totalOwnedOrAssignedCount: action.payload.totalOwnedOrAssignedCount
          };
        }
      )
      // 📌 Matchers for Loading & Error States
      .addMatcher(
        (action) => action.type.startsWith("tasks/") && action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("tasks/") && action.type.endsWith("/fulfilled"),
        (state) => {
          state.loading = false;
        }
      )
      .addMatcher(
        (action) => action.type.startsWith("tasks/") && action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || "An error occurred";
        }
      );
  },
});

export const { setNewTask, setTaskInfo, setNullInfo, setTaskReference, clearTaskReference, openTaskPopup, closeTaskPopup, setActionItemId, setCopiedAndRedirect, setRedirectFilter } = tasksSlice.actions;
export default tasksSlice.reducer;
